import React from "react";
import { motion } from "framer-motion";
import PageHeader from "@/components/PageHeader";
import SEO from "@/components/SEO";
import { Quote } from "lucide-react";

export default function About() {
    return (
        <div data-testid="about-page">
            <SEO
                title="About — Moving Mountains Advisors"
                description="Independent insurance advisors. Human guidance, AI-assisted. Founded by Jeremy Felix; led by President Brent Deparis."
            />
            <PageHeader
                eyebrow="About"
                title="Independent advisors. Human guidance, AI-assisted."
                subtitle="Moving Mountains was built on a simple idea: that insurance should feel less like a sales call and more like a conversation with someone who actually gives a damn."
            />

            <section className="mm-section bg-white">
                <div className="mm-container grid gap-14 lg:grid-cols-[1fr_1fr]">
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="mm-eyebrow mb-3">Our mission</div>
                        <h2 className="mm-display text-4xl text-mm-navy">
                            Protect what moves you — without the games.
                        </h2>
                        <p className="mt-5 text-mm-muted leading-relaxed">
                            We don't guarantee approval, quote rates over the phone, or
                            pressure clients into products they didn't ask about. What
                            we do: listen, match you with the carrier that fits, and
                            stay in your corner after the policy is in force.
                        </p>
                        <p className="mt-4 text-mm-muted leading-relaxed">
                            Our promise is simple — you'll always know why we're
                            recommending something, what alternatives exist, and what
                            could change over time. That's it.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.97 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="mm-card p-8"
                    >
                        <Quote className="text-mm-sky" />
                        <p className="mm-display mt-4 text-2xl leading-snug text-mm-navy">
                            &ldquo;The goal isn't to sell a policy. The goal is to
                            leave every family more protected than we found them.&rdquo;
                        </p>
                        <p className="mt-4 text-sm text-mm-muted">
                            — Brent Deparis, President
                        </p>
                    </motion.div>
                </div>
            </section>

            <section className="mm-section bg-mm-bg">
                <div className="mm-container">
                    <div className="mm-eyebrow mb-3">Leadership</div>
                    <h2 className="mm-display text-3xl sm:text-4xl text-mm-navy">
                        The people behind Moving Mountains.
                    </h2>
                    <div className="mt-10 grid gap-8 md:grid-cols-2">
                        <LeadershipCard
                            testid="about-brent"
                            photo="/brand/brent.jpg"
                            alt="Brent Deparis, President of Moving Mountains Advisors"
                            eyebrow="President"
                            name="Brent Deparis"
                            bio="Brent leads client relationships and advisor coordination at Moving Mountains. He's known for a calm, unhurried style and a focus on long-term fit over quick sales."
                        />
                        <LeadershipCard
                            testid="about-jeremy"
                            photo="/brand/jeremy.jpg"
                            alt="Jeremy Felix, Founder of Jarvis and CEO of Moving Mountains Advisors"
                            eyebrow="Founder of Jarvis & CEO"
                            name="Jeremy Felix"
                            bio="Jeremy built Jarvis and the firm's back-office technology from the ground up, with a single focus: remove the friction between a prospective client and a straight answer."
                            license="Licensed 215 (FL)"
                        />
                    </div>
                </div>
            </section>

            <section className="mm-section bg-white">
                <div className="mm-container max-w-3xl">
                    <div className="mm-eyebrow mb-3">Jarvis, origin story</div>
                    <h2 className="mm-display text-3xl sm:text-4xl text-mm-navy">
                        Why we built an AI concierge.
                    </h2>
                    <p className="mt-5 text-mm-muted leading-relaxed">
                        Most prospective clients wait days for a callback, then spend
                        40 minutes on a discovery call repeating the same basics.
                        Jarvis is our answer: a thoughtful, AI-powered concierge that
                        gathers what's relevant and hands it to a licensed advisor
                        already briefed.
                    </p>
                    <p className="mt-4 text-mm-muted leading-relaxed">
                        Jarvis will never quote you a rate or promise approval — those
                        are always human-and-carrier decisions. What Jarvis does is
                        respect your time.
                    </p>
                    <p className="mt-5 mm-disclaimer">
                        This experience is powered by advanced AI technology. A
                        licensed advisor reviews every recommendation.
                    </p>
                </div>
            </section>
        </div>
    );
}

function LeadershipCard({ testid, photo, alt, eyebrow, name, bio, license }) {
    return (
        <article className="mm-card flex flex-col items-center p-7 text-center sm:items-start sm:text-left" data-testid={testid}>
            <div className="relative">
                <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-mm-navy via-mm-blue to-mm-sky opacity-90 blur-[0.5px]" />
                <div className="relative h-[200px] w-[200px] overflow-hidden rounded-full border-4 border-white shadow-md sm:h-[280px] sm:w-[280px]">
                    <img
                        src={photo}
                        alt={alt}
                        className="h-full w-full object-cover object-[center_top]"
                        loading="lazy"
                        width="280"
                        height="280"
                    />
                </div>
            </div>
            <div className="mm-eyebrow mt-6">{eyebrow}</div>
            <h3 className="mm-display mt-1 text-2xl text-mm-navy">{name}</h3>
            <p className="mt-3 text-sm text-mm-muted leading-relaxed">{bio}</p>
            {license ? (
                <div className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-mm-line bg-mm-bg px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-mm-navy">
                    {license}
                </div>
            ) : null}
        </article>
    );
}
